import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import searchIcon from "assets/icons/icon-search-blue.svg";
import closeIcon from "assets/icons/close-icon-blue.svg";
import { RootState } from "redux/reducers";
import { searchReport, resetReportSearch } from "redux/actions/report";
import { clearErrors } from "redux/actions/errors";
import { useQuery } from "utils/search.utils";
import { useAuth } from "contexts/AuthContext";
import ModeSwitch, {
  SearchMode,
} from "components/Header/ChildComponents/ModeSwitch";

const ReportSearch = () => {
  const dispatch = useDispatch();
  const params = useQuery();
  const term = params.get("term");
  const linkMode = params.get("mode");
  const { id = "" } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const { report, errors } = useSelector((state: RootState) => state);
  const headerMode = useSelector(
    (state: RootState) => state.search.mode || "exact"
  );
  // Starts as the search that opened the report (the link's mode), else the
  // header's switch; after that the two switches change separately
  const [mode, setMode] = useState<SearchMode>(
    linkMode === "fuzzy" || linkMode === "exact" ? linkMode : headerMode
  );
  const [query, setQuery] = useState<string>("");
  const searchedMode = useRef<SearchMode>(mode);
  // Worked out from the search, not kept in errors: the report page clears
  // errors whenever the report changes (BookmarkModal)
  const message =
    errors.search ||
    (report.search.query && !report.search.chapters.length
      ? `There are no results for “${report.search.query}” in this report.`
      : "");

  const runSearch = (text: string, searchMode: SearchMode) => {
    dispatch(clearErrors());
    dispatch(
      searchReport(
        report.reportContentData,
        id,
        currentUser.usergroupData.id,
        text,
        searchMode
      )
    );
  };

  // A change of the report's switch searches the report again
  useEffect(() => {
    if (mode === searchedMode.current) return;
    searchedMode.current = mode;
    if (report.search.query) runSearch(report.search.query, mode);
  }, [mode]);

  // Opened from a search: the link has the words and the mode
  useEffect(() => {
    dispatch(resetReportSearch(report));

    if (term) {
      setQuery(term);
      setTimeout(() => runSearch(term, mode), 0);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearErrors());
    if (!query) return;

    runSearch(query, mode);
  };

  const handleReset = async () => {
    setQuery("");
    dispatch(await resetReportSearch(report));
  };

  return (
    <>
      <Switch
        mode={mode}
        setMode={setMode}
        light
      />
      <Container autoComplete="off" onSubmit={handleSubmit}>
        <Input
          type="text"
          onChange={(e) => setQuery(e.target.value)}
          value={query}
          placeholder="Search This Report"
          size={15}
          // disabled={report.search.query}
        />
        {!report.search.query ? (
          <SearchButton type="submit">
            <img src={searchIcon} alt="search glass" />
          </SearchButton>
        ) : (
          <SearchButton type="reset" onClick={handleReset}>
            <img src={closeIcon} alt="close icon" />
          </SearchButton>
        )}
      </Container>
      {/* Under the field, not in it: the field hides what overflows it */}
      {message ? (
        <Error role="status">{message}</Error>
      ) : (
        report.search.correctedQuery && (
          <Hint>({report.search.correctedQuery})</Hint>
        )
      )}
    </>
  );
};

const Container = styled.form`
  border: 1px solid ${(props) => props.theme.colors.idataBlue};
  border-radius: 5rem;
  display: flex;
  align-items: center;
  position: relative;
  height: 7rem;
  // flex: 2 1 0;
  overflow: hidden;
`;

const Input = styled.input`
  background-color: transparent;
  color: ${(props) => props.theme.colors.idataBlue};
  font-size: 1.8rem;
  height: 6rem;
  flex: 2 1 0;
  padding: 0rem 2rem;
  padding-right: 0;
  height: 6rem;
  width: 27rem;

  &::placeholder {
    color: ${(props) => props.theme.colors.idataBlue};
  }

  @media screen and (max-width: 650px) {
    font-size: 1.6rem;
  }
`;

// Above the field, which is too narrow for it
const Switch = styled(ModeSwitch)`
  align-self: flex-end;
`;

const SearchButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0 1.75rem;
  height: 100%;
  display: flex;
  background-color: rgba(0, 0, 0, 0.05);

  > img {
    margin: auto;
    height: 3rem;
  }

  &:hover {
    opacity: 0.5;
  }
`;

// The corrected words a search with a typo shows, under the words as typed
const Hint = styled.p`
  color: ${(props) => props.theme.colors.idataBlue};
  font-size: 1.6rem;
  margin: 0;
  text-align: center;
`;

const Error = styled.p`
  color: ${(props) => props.theme.colors.red};
  font-size: 1.6rem;
  margin: 0;
  text-align: center;
`;

export default ReportSearch;
